import {
  ESdChangeReason,
  IEffectsEntryAnimation,
  IKuikaStyle,
  InvisibleStylePart,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSlider from "../../../ui-kit/kuika-slider/kuika-slider";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import PopoverInput from "./popover-input/popover-input";
import {
  buildEntryAnimationFromType,
  detectEntryAnimationType,
  ENTRY_ANIMATION_DEFAULT_DURATION,
  ENTRY_ANIMATION_DEFAULT_EASING,
  ENTRY_ANIMATION_TYPE_OPTIONS,
  EntryAnimationType,
  summarizeEntryAnimationTiming,
  TRANSITION_EASING_OPTIONS
} from "./styling-effects-helpers";
import { styleSectionNeedsRender } from "./styling-window-helpers";

const DURATION_MIN: number = 0;
const DURATION_MAX: number = 5000;
const DELAY_MIN: number = 0;
const DELAY_MAX: number = 5000;

interface EntryTimingPopoverContentProps {
  item: IEffectsEntryAnimation;
  onPatch: (patch: Partial<IEffectsEntryAnimation>) => void;
}

const EntryTimingPopoverContent: React.FC<EntryTimingPopoverContentProps> = ({ item, onPatch }) => {
  return (
    <div className="effects-transition__popover">
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Duration</KuikaSmallText>
        <KuikaSlider
          value={item.durationMs ?? ENTRY_ANIMATION_DEFAULT_DURATION}
          onChange={(next: number): void => onPatch({ durationMs: next })}
          min={DURATION_MIN}
          max={DURATION_MAX}
          step={10}
          precision={0}
          unit="ms"
          size="sm"
          dataTestId="effects-entry-duration"
        />
      </div>
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Easing</KuikaSmallText>
        <KuikaSelectbox
          size="sm"
          options={TRANSITION_EASING_OPTIONS}
          value={item.timingFunction ?? ENTRY_ANIMATION_DEFAULT_EASING}
          onChange={(next: string | number | (string | number)[] | null | any): void => {
            if (typeof next === "string") onPatch({ timingFunction: next });
          }}
          dataTestId="effects-entry-easing"
        />
      </div>
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Delay</KuikaSmallText>
        <KuikaInput
          type="number"
          size="sm"
          value={item.delayMs ?? 0}
          rightUnit="ms"
          min={DELAY_MIN}
          max={DELAY_MAX}
          onChange={(_e, next: string | number): void => {
            const num: number = typeof next === "number" ? next : Number(next);
            if (!Number.isNaN(num)) onPatch({ delayMs: num });
          }}
          data-testid="effects-entry-delay"
        />
      </div>
    </div>
  );
};

export interface IEffectsEntryAnimationComponentProps {
  form: FormInstance<IKuikaStyle>;
}

interface IEffectsEntryAnimationComponentState {
  placeholder: never[];
}

class EffectsEntryAnimationComponent extends PureSdComponent<
  IEffectsEntryAnimationComponentProps & IWithSDProps,
  IEffectsEntryAnimationComponentState
> {
  constructor(props: IEffectsEntryAnimationComponentProps & IWithSDProps) {
    super(props);
    this.state = { placeholder: [] };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  private getCurrent = (): IEffectsEntryAnimation | null =>
    (this.props.form.getFieldValue(["effects", "entryAnimation"]) as IEffectsEntryAnimation | null | undefined) ?? null;

  private commit = (next: IEffectsEntryAnimation | null): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!fieldsValue.effects) {
      fieldsValue.effects = { effectsId: null, kuikaid: kuikaid ?? "" };
    }
    fieldsValue.effects.entryAnimation = next;
    // Same persistence contract as styling-effects-transitions-component:
    // setFieldsValue + submit() persists via styling-window's
    // handleStyleFormFinish. Never call resetFields() — it unmounts the
    // Form.Item subtree and closes the timing popover mid-edit.
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  private handleTypeChange = (type: EntryAnimationType): void => {
    const current: IEffectsEntryAnimation | null = this.getCurrent();
    const durationMs: number = current?.durationMs ?? ENTRY_ANIMATION_DEFAULT_DURATION;
    const timingFunction: string = current?.timingFunction ?? ENTRY_ANIMATION_DEFAULT_EASING;
    const delayMs: number = current?.delayMs ?? 0;
    this.commit(buildEntryAnimationFromType(type, durationMs, timingFunction, delayMs));
  };

  private patchTiming = (patch: Partial<IEffectsEntryAnimation>): void => {
    const current: IEffectsEntryAnimation | null = this.getCurrent();
    if (!current) return;
    this.commit({ ...current, ...patch });
  };

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }

    return (
      <Form.Item noStyle shouldUpdate>
        {() => {
          const current: IEffectsEntryAnimation | null = this.getCurrent();
          const type: EntryAnimationType = detectEntryAnimationType(current);
          return (
            <div className="effects-section__entry">
              {/* Label-left / control-right row, same shape as Outline & Blending. */}
              <div className="kuika-styling-panel__form-field-wrapper">
                <KuikaSmallMediumText>Entry Animation</KuikaSmallMediumText>
                <KuikaSelectbox
                  size="sm"
                  options={ENTRY_ANIMATION_TYPE_OPTIONS}
                  value={type}
                  onChange={(next: string | number | (string | number)[] | null | any): void => {
                    if (typeof next === "string") this.handleTypeChange(next as EntryAnimationType);
                  }}
                  data-testid="effects-entry-type"
                />
              </div>
              {type !== "none" && current && (
                <div className="effects-section__entry-timing">
                  <PopoverInput
                    value={summarizeEntryAnimationTiming(current)}
                    placeholder="Timing"
                    onClear={(): void => this.commit(null)}
                    popoverContent={<EntryTimingPopoverContent item={current} onPatch={this.patchTiming} />}
                    popoverTitle="Entry Animation"
                    popoverPosition="left"
                    popoverAlign="end"
                    dataTestId="effects-entry-timing"
                  />
                </div>
              )}
            </div>
          );
        }}
      </Form.Item>
    );
  }
}

export default withSD<IEffectsEntryAnimationComponentProps>(EffectsEntryAnimationComponent);
